import { invitationAcceptSchema, ROLE_LABELS } from "@pcj/shared";
import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Banner, Button, Field, inputClass, PageLoader } from "../../components/ui";
import { api, errorMessage } from "../../lib/api";
import { authClient, authErrorMessage } from "../../lib/auth-client";
import { queryKeys, usePublicInvitation } from "../../lib/queries";
import { AuthLayout } from "./AuthLayout";

export function InvitationPage() {
  const { token = "" } = useParams();
  const invitation = usePublicInvitation(token);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (invitation.isPending) return <PageLoader />;
  if (invitation.isError || !invitation.data.valid) {
    return (
      <AuthLayout title="Invitación no válida">
        <Banner tone="error">
          {invitation.isError
            ? errorMessage(invitation.error)
            : "La invitación venció o ya fue usada. Pide a tu Responsable de Cumplimiento que te envíe una nueva."}
        </Banner>
        <p className="mt-4 text-xs text-gray-600">
          <Link to="/login" className="underline hover:text-gray-900">
            Ir a iniciar sesión
          </Link>
        </p>
      </AuthLayout>
    );
  }
  const { email, companyName, role } = invitation.data;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    const parsed = invitationAcceptSchema.safeParse({ name, password });
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? "Revisa los datos");
    if (password !== confirm) return setError("Las contraseñas no coinciden.");
    setBusy(true);
    try {
      await api.post(`/invitations/${token}/accept`, parsed.data);
      const { error: signInError } = await authClient.signIn.email({ email, password });
      if (signInError) throw new Error(authErrorMessage(signInError));
      await queryClient.invalidateQueries({ queryKey: queryKeys.me });
      navigate("/", { replace: true });
    } catch (acceptError) {
      setError(errorMessage(acceptError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title={`Únete a ${companyName}`}
      subtitle={`Te invitaron como ${ROLE_LABELS[role]}. Tu cuenta será ${email}.`}
    >
      <form onSubmit={submit} className="space-y-3">
        <Field label="Tu nombre">
          {(id) => (
            <input
              id={id}
              autoComplete="name"
              required
              className={inputClass}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>
        <Field label="Crea una contraseña" hint="Mínimo 10 caracteres.">
          {(id) => (
            <input
              id={id}
              type="password"
              autoComplete="new-password"
              required
              className={inputClass}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </Field>
        <Field label="Confirma la contraseña">
          {(id) => (
            <input
              id={id}
              type="password"
              autoComplete="new-password"
              required
              className={inputClass}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          )}
        </Field>
        {error && <Banner tone="error">{error}</Banner>}
        <Button type="submit" loading={busy} className="w-full">
          Aceptar invitación
        </Button>
      </form>
    </AuthLayout>
  );
}
